/**
 * Public read layer for CMS-managed content.
 *
 * Every hook returns data in exactly the shape the pages already used when they
 * read from the static files in src/data/, so the components stay unchanged
 * apart from swapping the import for a hook.
 *
 * If the API is unreachable (or Amplify was never configured, e.g. before
 * `npx ampx sandbox` has run), each hook falls back to the bundled static data
 * rather than rendering an empty page. The public site therefore never goes
 * blank because of a backend problem.
 *
 * Reads use the public API key auth mode — visitors are not signed in.
 */
import { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import outputs from '../amplify_outputs.json';

// Extensions are explicit throughout this project: there is no tsconfig.json,
// so CRA does not add .ts/.tsx to its module resolution extensions.
import { ministries as staticMinistries } from './ministries.tsx';
import { newsletters as staticNewsletters } from './newletters.tsx';
import { galleryMedia as staticGallery } from './gallery.tsx';

export interface MinistryItem {
  title: string;
  description: string;
}

export interface NewsletterItem {
  url: string;
  description: string;
  image: string;
}

export interface GalleryItem {
  /**
   * S3 key of the original. Null for the bundled fallback data, which already
   * carries absolute URLs.
   */
  storageKey: string | null;
  /**
   * What the grid renders: a web-sized copy of a photo, or a poster frame for
   * a video. Equal to the full URL only for the bundled fallback data.
   */
  thumbUrl: string;
  /** Present only when the full-size URL is already known (fallback data). */
  url?: string;
  type: 'image' | 'video';
}


export interface BlogPostItem {
  id: string;
  title: string;
  body: string;
  author?: string;
  publishedAt?: string;
  coverUrl?: string;
  tags?: string[];
}

/** A page's editable content, keyed by contentKey. */
export type PageContentMap = Record<string, string>;

export interface ContentState<T> {
  items: T[];
  loading: boolean;
  /** True when the static bundled data is being shown because the API failed. */
  usedFallback: boolean;
}

// The models are built at runtime from the model_introspection block in
// amplify_outputs.json. The generated Schema type lives outside src/, which
// Create React App refuses to import, so the client is typed structurally here.
type ListResult = { data: any[]; errors?: unknown[] };
type Model = { list: (args?: any) => Promise<ListResult> };

let cachedClient: { models: Record<string, Model> } | null = null;

const getClient = () => {
  if (!cachedClient) {
    cachedClient = generateClient() as unknown as { models: Record<string, Model> };
  }
  return cachedClient;
};

/**
 * Public URL for an object under a publicly readable prefix.
 *
 * Deliberately NOT Amplify's getUrl(): that presigns, which costs an SigV4
 * signature per object in the browser. On the gallery that measured 14.4
 * seconds of stalled CPU for 87 images before a single request went out.
 * The thumbnails/* and gallery/* prefixes are granted anonymous GetObject in
 * amplify/backend.ts, so a plain URL works — instant, cacheable, no expiry.
 *
 * Each path segment is encoded individually so that '/' separators survive
 * while spaces and other characters in filenames are escaped.
 */
const bucketConfig = (outputs as any)?.storage ?? {};

export const publicUrlFor = (key: string): string => {
  const { bucket_name: bucketName, aws_region: region } = bucketConfig;
  if (!bucketName || !region) return '';
  const encoded = key.split('/').map(encodeURIComponent).join('/');
  return `https://${bucketName}.s3.${region}.amazonaws.com/${encoded}`;
};

/** Kept async so callers need not change if signing is ever reinstated. */
const resolveKey = async (key: string): Promise<string> => publicUrlFor(key);

/**
 * Public URL of the generated thumbnail for an original object key.
 * Used by the CMS list views to show a preview without a signing round trip.
 */
export const publicThumbnailUrl = (key: string): string =>
  key ? publicUrlFor(thumbnailKeyFor(key)) : '';

/**
 * Key of the generated thumbnail for an original object.
 * Mirrors THUMBNAIL_PREFIX in amplify/functions/thumbnail-generator/handler.ts.
 */
const thumbnailKeyFor = (key: string): string => `thumbnails/${key}`;

/**
 * Full-size URL for a gallery item, resolved only when actually needed
 * (opening the lightbox, or advancing the carousel).
 *
 * Signing happens client-side, so resolving both sizes for every item up front
 * meant ~174 signatures before the first tile could paint. Grid tiles only ever
 * need the thumbnail, so the original is deferred and memoised here.
 */
const originalUrlCache = new Map<string, Promise<string>>();

export const resolveOriginalUrl = (item: GalleryItem): Promise<string> => {
  if (item.url) return Promise.resolve(item.url);
  if (!item.storageKey) return Promise.resolve(item.thumbUrl);

  const cached = originalUrlCache.get(item.storageKey);
  if (cached) return cached;

  const pending = resolveKey(item.storageKey);
  originalUrlCache.set(item.storageKey, pending);
  return pending;
};

/** Fetches every page of a model, sorted by sortOrder (nulls last). */
const listAll = async (modelName: string): Promise<any[]> => {
  const model = getClient().models[modelName];
  if (!model) throw new Error(`Model ${modelName} is not available on the client`);

  const rows: any[] = [];
  let nextToken: string | null = null;
  do {
    const page: any = await model.list({ limit: 200, nextToken, authMode: 'apiKey' });
    if (page.errors?.length) throw new Error(JSON.stringify(page.errors));
    rows.push(...page.data);
    nextToken = page.nextToken ?? null;
  } while (nextToken);

  return rows.sort((a, b) => {
    const av = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bv = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return av - bv;
  });
};

/**
 * Shared hook body: run `load`, fall back to `fallback` on any failure.
 * `enabled` short-circuits the request when Amplify has no configuration.
 */
const useContent = <T,>(
  label: string,
  load: () => Promise<T[]>,
  fallback: T[]
): ContentState<T> => {
  const [state, setState] = useState<ContentState<T>>({
    items: [],
    loading: true,
    usedFallback: false,
  });

  useEffect(() => {
    let cancelled = false;

    load()
      .then((items) => {
        if (cancelled) return;
        // An empty table almost always means "not seeded yet" rather than
        // "the ministry deleted everything", so prefer the static content.
        if (items.length === 0) {
          setState({ items: fallback, loading: false, usedFallback: true });
          return;
        }
        setState({ items, loading: false, usedFallback: false });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(`[cms] falling back to bundled ${label}:`, err);
        setState({ items: fallback, loading: false, usedFallback: true });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
};

export const useMinistries = (): ContentState<MinistryItem> =>
  useContent<MinistryItem>(
    'ministries',
    async () =>
      (await listAll('Ministry')).map((m) => ({
        title: m.title,
        description: m.description,
      })),
    staticMinistries
  );

export const useNewsletters = (): ContentState<NewsletterItem> =>
  useContent<NewsletterItem>(
    'newsletters',
    async () => {
      const rows = await listAll('Newsletter');
      return Promise.all(
        rows.map(async (n) => ({
          url: n.url,
          description: n.title,
          // Newsletter cards render small — the thumbnail is plenty.
          image: n.thumbnailKey ? await resolveKey(thumbnailKeyFor(n.thumbnailKey)) : '',
        }))
      );
    },
    staticNewsletters
  );

export const useGalleryMedia = (): ContentState<GalleryItem> =>
  useContent<GalleryItem>(
    'gallery',
    async () => {
      const rows = await listAll('GalleryPhoto');
      return Promise.all(
        rows.map(async (g) => {
          // Videos carry a poster frame at the same thumbnails/<key> location as
          // photo thumbnails, so both kinds of tile load an image and the video
          // file itself is only fetched when opened.
          return {
            storageKey: g.imageKey,
            thumbUrl: await resolveKey(thumbnailKeyFor(g.imageKey)),
            type: g.mediaType === 'VIDEO' ? ('video' as const) : ('image' as const),
          };
        })
      );
    },
    (staticGallery as { url: string; type: 'image' | 'video' }[]).map((g) => ({
      storageKey: null,
      thumbUrl: g.url,
      url: g.url,
      type: g.type,
    }))
  );


/**
 * Blog posts for the public site.
 *
 * Drafts stay hidden: a post appears once it has a publish date that is not in
 * the future, so the ministry can write ahead of time.
 */
export const useBlogPosts = (): ContentState<BlogPostItem> =>
  useContent<BlogPostItem>(
    'blog posts',
    async () => {
      const rows = await listAll('BlogPost');
      const now = Date.now();
      const published = rows.filter(
        (p) => p.publishedAt && new Date(p.publishedAt).getTime() <= now
      );
      published.sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
      return published.map((p) => ({
        id: p.id,
        title: p.title,
        body: p.body,
        author: p.author ?? undefined,
        publishedAt: p.publishedAt ?? undefined,
        coverUrl: p.coverImageKey ? publicThumbnailUrl(p.coverImageKey) : undefined,
        tags: p.tags ?? undefined,
      }));
    },
    []
  );

/**
 * Editable copy for one page, as a { contentKey: value } map.
 *
 * Pages call this and then fall back to their built-in wording for any key the
 * ministry has not filled in, so an empty table changes nothing on the site.
 */
export const usePageContent = (page: 'HOME' | 'ABOUT' | 'DONATE' | 'CONTACT'): PageContentMap => {
  const [values, setValues] = useState<PageContentMap>({});

  useEffect(() => {
    let cancelled = false;
    listAll('PageContent')
      .then((rows) => {
        if (cancelled) return;
        const map: PageContentMap = {};
        for (const row of rows) {
          if (row.page === page && row.contentKey && row.value) {
            map[row.contentKey] = row.value;
          }
        }
        setValues(map);
      })
      .catch((err) => {
        // Non-fatal: the page keeps its built-in copy.
        console.warn('[cms] page content unavailable:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  return values;
};
